import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  User,
  Settings,
  LogOut,
  Bell,
  Globe,
  QrCode,
  Calendar,
  FileText,
  Heart,
  Video,
  Mic,
  MicOff,
  VideoOff,
  Users,
  Clock,
  Star,
  Download,
  Share,
  Plus,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Check,
  AlertTriangle,
  Info,
} from "lucide-react";
import quantumLogo from "../../quantum-care-logo.png";

// Doctors data for appointment booking
const doctors = [
  {
    name: "Dr. Emily Chen",
    specialty: "General Practice",
    rating: 5.0,
    reviews: 127,
    image: "https://randomuser.me/api/portraits/women/44.jpg",
    times: ["10:00 AM", "11:30 AM", "1:00 PM"],
  },
  {
    name: "Dr. Michael Rodriguez",
    specialty: "Internal Medicine",
    rating: 5.0,
    reviews: 89,
    image: "https://randomuser.me/api/portraits/men/45.jpg",
    times: ["10:00 AM", "11:30 AM", "1:00 PM"],
  },
  {
    name: "Dr. Sarah Thompson",
    specialty: "Family Medicine",
    rating: 5.0,
    reviews: 156,
    image: "https://randomuser.me/api/portraits/women/65.jpg",
    times: ["10:00 AM", "11:30 AM", "1:00 PM"],
  },
];

function Dashboard() {
  const Navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState("home");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState("en");
  const [chatMessages, setChatMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [symptoms, setSymptoms] = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // Mock Data
  const mockUser = {
    name: "Ahmed Hassan",
    email: "ahmed.hassan@example.mv",
    avatar: "/api/placeholder/40/40",
    role: "Patient",
    mrn: "MRN-2024-001",
    phone: "+960 123 4567",
    status: "online",
  };

  const mockAppointments = [
    {
      id: "1",
      doctor: "Dr. Aisha Ibrahim",
      date: "2024-01-20",
      time: "14:00",
      type: "video",
      status: "upcoming",
    },
    {
      id: "2",
      doctor: "Dr. Mohamed Ali",
      date: "2024-01-15",
      time: "10:30",
      type: "chat",
      status: "completed",
    },
  ];

  // Handle appointment slot selection
  const handleSlotSelect = (doctor, time) => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString();
    setSelectedAppointment({
      doctor: doctor.name,
      time,
      date: `Today, ${formattedDate}`,
    });
  };

  // Navigation Component (unchanged)
  const Navigation = () => (
    <nav className="bg-slate-900 border-b border-slate-700 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <img src={quantumLogo} style={{ width: "200px" }} />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block ml-10">
              <div className="flex items-baseline space-x-4">
                <button
                  onClick={() => setCurrentPage("home")}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === "home"
                      ? "bg-slate-700 text-white"
                      : "text-slate-300 hover:text-white hover:bg-slate-700"
                  }`}
                >
                  Home
                </button>
                <button
                  onClick={() => setCurrentPage("appointments")}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === "appointments"
                      ? "bg-slate-700 text-white"
                      : "text-slate-300 hover:text-white hover:bg-slate-700"
                  }`}
                >
                  Appointments
                </button>
                <button
                  onClick={() => setCurrentPage("records")}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === "records"
                      ? "bg-slate-700 text-white"
                      : "text-slate-300 hover:text-white hover:bg-slate-700"
                  }`}
                >
                  Health Records
                </button>
              </div>
            </div>
          </div>

          {/* Right side - Language toggle, notifications, and user menu */}
          <div className="flex items-center space-x-4">
            {/* Language Toggle */}
            <button
              onClick={() => setLanguage(language === "en" ? "dv" : "en")}
              className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
              title="Switch Language"
            >
              <Globe className="h-5 w-5" />
            </button>

            {/* Notifications */}
            <button className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition-colors relative">
              <Bell className="h-5 w-5" />
              <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center space-x-3 p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
              >
                <img
                  src="https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop"
                  alt={mockUser.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-white">
                    {mockUser.name}
                  </p>
                  <p className="text-xs text-slate-400">{mockUser.role}</p>
                </div>
                <ChevronDown className="h-4 w-4" />
              </button>

              {/* Dropdown Menu */}
              {userDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                  <button
                    onClick={() => {
                      setCurrentPage("profile");
                      setUserDropdownOpen(false);
                    }}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                  >
                    <User className="mr-3 h-4 w-4" />
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      setCurrentPage("settings");
                      setUserDropdownOpen(false);
                    }}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                  >
                    <Settings className="mr-3 h-4 w-4" />
                    Settings
                  </button>
                  <hr className="my-1" />
                  <button
                    className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                    onClick={() => {
                      Navigate("/");
                    }}
                  >
                    <LogOut className="mr-3 h-4 w-4" />
                    Log out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-slate-800 border-t border-slate-700">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <button
              onClick={() => {
                setCurrentPage("home");
                setIsMobileMenuOpen(false);
              }}
              className="block px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:text-white hover:bg-slate-700 w-full text-left"
            >
              Home
            </button>
            <button
              onClick={() => {
                setCurrentPage("appointments");
                setIsMobileMenuOpen(false);
              }}
              className="block px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:text-white hover:bg-slate-700 w-full text-left"
            >
              Appointments
            </button>
            <button
              onClick={() => {
                setCurrentPage("records");
                setIsMobileMenuOpen(false);
              }}
              className="block px-3 py-2 rounded-md text-base font-medium text-slate-300 hover:text-white hover:bg-slate-700 w-full text-left"
            >
              Health Records
            </button>
          </div>
        </div>
      )}
    </nav>
  );

  // Book Appointment Page Component (NEW)
  const BookAppointmentPage = () => (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      <div className="bg-slate-50 min-h-screen flex items-center justify-center py-10">
        <div className="bg-white rounded-xl shadow-md p-6 w-full max-w-6xl">
          {/* Header */}
          <h2 className="text-2xl font-bold text-gray-800 mb-1">
            Book Your Appointment
          </h2>
          <p className="text-gray-500 mb-4">
            Select a doctor and available time slot
          </p>
          <hr className="mb-6" />

          {/* Doctor Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {doctors.map((doctor, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-xl p-5 text-center shadow-sm hover:shadow-md transition"
              >
                <img
                  src={doctor.image}
                  alt={doctor.name}
                  className="w-16 h-16 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-lg font-semibold text-gray-900">
                  {doctor.name}
                </h3>
                <p className="text-gray-500">{doctor.specialty}</p>
                <p className="flex items-center justify-center text-yellow-500 mt-1">
                  <span className="text-lg">★</span>
                  <span className="ml-1 text-gray-700 font-medium">
                    {doctor.rating.toFixed(1)}
                  </span>
                  <span className="ml-1 text-gray-500">
                    ({doctor.reviews} reviews)
                  </span>
                </p>

                {/* Time Slots */}
                <div className="flex flex-wrap gap-2 mt-4 justify-center">
                  {doctor.times.map((time, tIdx) => (
                    <button
                      key={tIdx}
                      onClick={() => handleSlotSelect(doctor, time)}
                      className={`border border-gray-300 rounded-md px-4 py-1 text-sm font-medium transition 
                        ${
                          selectedAppointment?.doctor === doctor.name &&
                          selectedAppointment?.time === time
                            ? "bg-blue-600 text-white border-blue-600"
                            : "hover:bg-blue-50 hover:border-blue-400"
                        }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Appointment Confirmation */}
          {selectedAppointment && (
            <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-bold text-blue-800 mb-2">
                Appointment Confirmation
              </h3>
              <p className="text-gray-700">
                <strong>Doctor:</strong> {selectedAppointment.doctor}
              </p>
              <p className="text-gray-700">
                <strong>Time:</strong> {selectedAppointment.time}
              </p>
              <p className="text-gray-700">
                <strong>Date:</strong> {selectedAppointment.date}
              </p>
              <button
                onClick={() => alert("Appointment Confirmed!")}
                className="mt-4 w-full bg-blue-600 text-white py-2 rounded-md font-medium hover:bg-blue-700 transition"
              >
                Confirm Appointment
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Home Page Component (updated to include book appointment button)
  const HomePage = () => (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome back, {mockUser.name.split(" ")[0]}
          </h1>
          <p className="text-slate-400">
            How can we help you with your health today?
          </p>
        </div>

        {/* Main Action Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Call Now Card */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-8 hover:from-blue-500 hover:to-blue-600 transition-all duration-300 cursor-pointer group shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-4">
                  <Phone className="h-8 w-8 text-white mr-3" />
                  <h2 className="text-2xl font-bold text-white">Call Now</h2>
                </div>
                <p className="text-blue-100 mb-4">
                  Connect instantly with available doctors
                </p>
                <div className="flex items-center text-blue-100 text-sm">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  3 doctors available
                </div>
              </div>
              <Video className="h-16 w-16 text-blue-200 group-hover:scale-110 transition-transform" />
            </div>
          </div>

          {/* Chat Card */}
          <div
            onClick={() => setCurrentPage("chat")}
            className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl p-8 hover:from-teal-500 hover:to-teal-600 transition-all duration-300 cursor-pointer group shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-4">
                  <MessageCircle className="h-8 w-8 text-white mr-3" />
                  <h2 className="text-2xl font-bold text-white">
                    Chat with AI
                  </h2>
                </div>
                <p className="text-teal-100 mb-4">
                  Describe your symptoms and get instant guidance
                </p>
                <div className="flex items-center text-teal-100 text-sm">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  AI assistant online
                </div>
              </div>
              <MessageCircle className="h-16 w-16 text-teal-200 group-hover:scale-110 transition-transform" />
            </div>
          </div>
        </div>

        {/* Digital Health Card and Quick Actions */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Digital Health Card */}
          <div className="lg:col-span-2 bg-slate-900 rounded-xl p-6 border border-slate-700">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-xl font-semibold text-white">
                Digital Health Card
              </h3>
              <QrCode className="h-6 w-6 text-slate-400" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-400">Full Name</p>
                    <p className="text-white font-medium">{mockUser.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">
                      Medical Record Number
                    </p>
                    <p className="text-white font-medium">{mockUser.mrn}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Phone</p>
                    <p className="text-white font-medium">{mockUser.phone}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <div className="w-32 h-32 bg-white rounded-lg p-4 flex items-center justify-center">
                  <QrCode className="w-full h-full text-slate-800" />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
              <button className="flex items-center px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors">
                <Share className="h-4 w-4 mr-2" />
                Share
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-4">
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-colors cursor-pointer">
              <div className="flex items-center mb-3">
                <Plus className="h-6 w-6 text-teal-400 mr-3" />
                <h4 className="font-semibold text-white">
                  Start New Assessment
                </h4>
              </div>
              <p className="text-slate-400 text-sm">Quick health screening</p>
            </div>

            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-colors cursor-pointer">
              <div className="flex items-center mb-3">
                <Calendar className="h-6 w-6 text-blue-400 mr-3" />
                <h4 className="font-semibold text-white">Next Appointment</h4>
              </div>
              <p className="text-slate-400 text-sm">Jan 20, 2:00 PM</p>
              <p className="text-slate-300 text-xs">Dr. Aisha Ibrahim</p>
            </div>

            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-colors cursor-pointer">
              <div className="flex items-center mb-3">
                <FileText className="h-6 w-6 text-purple-400 mr-3" />
                <h4 className="font-semibold text-white">Recent Visits</h4>
              </div>
              <p className="text-slate-400 text-sm">View medical history</p>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
          <h3 className="text-xl font-semibold text-white mb-4">
            Recent Activity
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-700 last:border-b-0">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                  <Check className="h-5 w-5 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-white font-medium">
                    Video consultation completed
                  </p>
                  <p className="text-slate-400 text-sm">
                    Dr. Mohamed Ali • Jan 15, 10:30 AM
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-400" />
            </div>

            <div className="flex items-center justify-between py-3 border-b border-slate-700 last:border-b-0">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-white font-medium">
                    Prescription received
                  </p>
                  <p className="text-slate-400 text-sm">
                    Medication for seasonal allergies
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // AI Chat Page Component (unchanged from your original)
  const ChatPage = () => (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Chat Interface */}
          <div className="lg:col-span-2">
            <div className="bg-slate-900 rounded-xl border border-slate-700 h-[600px] flex flex-col">
              {/* Chat Header */}
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      AI Health Assistant
                    </h2>
                    <div className="flex items-center mt-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                      <p className="text-sm text-slate-400">
                        Online and ready to help
                      </p>
                    </div>
                  </div>
                  {/* Step Indicator */}
                  <div className="flex items-center space-x-2">
                    <div className="text-sm text-slate-400">
                      Step {currentStep} of 4
                    </div>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4].map((step) => (
                        <div
                          key={step}
                          className={`w-2 h-2 rounded-full ${
                            step <= currentStep ? "bg-teal-400" : "bg-slate-600"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="space-y-4">
                  {/* AI Welcome Message */}
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="h-4 w-4 text-white" />
                    </div>
                    <div className="bg-slate-800 rounded-lg p-4 max-w-md">
                      <p className="text-white">
                        Hello! I'm your AI health assistant. I'll help you
                        describe your symptoms and guide you to the right care.
                        Let's start - what's bothering you today?
                      </p>
                    </div>
                  </div>

                  {/* User Messages */}
                  {chatMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.sender === "user"
                          ? "justify-end"
                          : "items-start space-x-3"
                      }`}
                    >
                      {message.sender === "ai" && (
                        <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <MessageCircle className="h-4 w-4 text-white" />
                        </div>
                      )}
                      <div
                        className={`rounded-lg p-4 max-w-md ${
                          message.sender === "user"
                            ? "bg-blue-600 text-white"
                            : "bg-slate-800 text-white"
                        }`}
                      >
                        <p>{message.text}</p>
                      </div>
                    </div>
                  ))}

                  {/* Sample conversation */}
                  {chatMessages.length === 0 && (
                    <div className="text-center py-8">
                      <MessageCircle className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">
                        Start by describing your symptoms...
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Input */}
              <div className="p-6 border-t border-slate-700">
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={symptoms}
                    onChange={(e) => setSymptoms(e.target.value)}
                    placeholder="Describe your symptoms..."
                    className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && symptoms.trim()) {
                        const newMessage = {
                          id: Date.now().toString(),
                          text: symptoms,
                          sender: "user",
                          timestamp: new Date(),
                        };
                        setChatMessages([...chatMessages, newMessage]);
                        setSymptoms("");
                        // Simulate AI response
                        setTimeout(() => {
                          const aiResponse = {
                            id: (Date.now() + 1).toString(),
                            text: "I understand you're experiencing those symptoms. Can you tell me more about when they started and how severe they are?",
                            sender: "ai",
                            timestamp: new Date(),
                          };
                          setChatMessages((prev) => [...prev, aiResponse]);
                        }, 1000);
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      if (symptoms.trim()) {
                        const newMessage = {
                          id: Date.now().toString(),
                          text: symptoms,
                          sender: "user",
                          timestamp: new Date(),
                        };
                        setChatMessages([...chatMessages, newMessage]);
                        setSymptoms("");
                      }
                    }}
                    className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg transition-colors"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-wrap gap-4">
              <button className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Video className="h-5 w-5 mr-2" />
                Start Video Call
              </button>
              <button
                onClick={() => setCurrentPage("bookappointment")}
                className="flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Calendar className="h-5 w-5 mr-2" />
                Book Appointment
              </button>
              <button className="flex items-center px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors">
                <FileText className="h-5 w-5 mr-2" />
                Save & Exit
              </button>
            </div>
          </div>

          {/* AI Insights Sidebar */}
          <div className="space-y-6">
            {/* Live Insights */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center mb-4">
                <div className="w-3 h-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                <h3 className="text-lg font-semibold text-white">
                  AI Insights (Live)
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2">
                    Symptom Summary
                  </h4>
                  <div className="text-sm text-slate-400">
                    {chatMessages.length > 0
                      ? "Analyzing your symptoms..."
                      : "No symptoms reported yet"}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2">
                    Suggested Questions
                  </h4>
                  <div className="space-y-2">
                    <div className="text-sm text-slate-400 p-2 bg-slate-800 rounded">
                      "When did the symptoms start?"
                    </div>
                    <div className="text-sm text-slate-400 p-2 bg-slate-800 rounded">
                      "How severe is the pain (1-10)?"
                    </div>
                    <div className="text-sm text-slate-400 p-2 bg-slate-800 rounded">
                      "Any recent travel or exposure?"
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2">
                    Possible Conditions
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-slate-800 rounded">
                      <span className="text-sm text-slate-400">
                        Common Cold
                      </span>
                      <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">
                        65%
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-slate-800 rounded">
                      <span className="text-sm text-slate-400">
                        Seasonal Allergies
                      </span>
                      <span className="text-xs bg-yellow-600 text-white px-2 py-1 rounded">
                        35%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Risk Assessment */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">
                Risk Assessment
              </h3>
              <div className="flex items-center p-4 bg-green-900/30 border border-green-700 rounded-lg">
                <Info className="h-5 w-5 text-green-400 mr-3" />
                <div>
                  <p className="text-green-400 font-medium">Low Risk</p>
                  <p className="text-sm text-green-300">
                    Symptoms suggest non-urgent condition
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <button className="w-full flex items-center p-3 text-left text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
                  <Phone className="h-4 w-4 mr-3" />
                  Emergency Call
                </button>
                <button className="w-full flex items-center p-3 text-left text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
                  <FileText className="h-4 w-4 mr-3" />
                  Medical History
                </button>
                <button className="w-full flex items-center p-3 text-left text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
                  <Users className="h-4 w-4 mr-3" />
                  Find Pharmacy
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Appointments Page Component (updated to include book appointment button)
  const AppointmentsPage = () => (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Appointments</h1>
          <button
            onClick={() => setCurrentPage("bookappointment")}
            className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Book New Appointment
          </button>
        </div>

        {/* Filters */}
        <div className="flex space-x-4 mb-6">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg">
            All
          </button>
          <button className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600">
            Upcoming
          </button>
          <button className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600">
            Past
          </button>
          <button className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600">
            Cancelled
          </button>
        </div>

        {/* Appointments List */}
        <div className="space-y-4">
          {mockAppointments.map((appointment) => (
            <div
              key={appointment.id}
              className="bg-slate-900 rounded-xl p-6 border border-slate-700"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    {appointment.type === "video" && (
                      <Video className="h-6 w-6 text-white" />
                    )}
                    {appointment.type === "chat" && (
                      <MessageCircle className="h-6 w-6 text-white" />
                    )}
                    {appointment.type === "phone" && (
                      <Phone className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {appointment.doctor}
                    </h3>
                    <p className="text-slate-400">
                      {appointment.date} at {appointment.time}
                    </p>
                    <div className="flex items-center mt-1">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          appointment.status === "upcoming"
                            ? "bg-green-900 text-green-300"
                            : appointment.status === "completed"
                            ? "bg-blue-900 text-blue-300"
                            : "bg-red-900 text-red-300"
                        }`}
                      >
                        {appointment.status.charAt(0).toUpperCase() +
                          appointment.status.slice(1)}
                      </span>
                      <span className="ml-2 text-xs text-slate-500 capitalize">
                        {appointment.type} Consultation
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {appointment.status === "upcoming" && (
                    <>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        Join
                      </button>
                      <button className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors">
                        Reschedule
                      </button>
                    </>
                  )}
                  {appointment.status === "completed" && (
                    <button className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
                      View Summary
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Health Records Page Component (unchanged from your original)
  const RecordsPage = () => (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Health Records</h1>
          <div className="flex space-x-3">
            <button className="flex items-center px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors">
              <Search className="h-4 w-4 mr-2" />
              Search
            </button>
            <button className="flex items-center px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-700 mb-8">
          <nav className="flex space-x-8">
            <button className="py-2 px-1 border-b-2 border-teal-400 text-teal-400 font-medium">
              Prescriptions
            </button>
            <button className="py-2 px-1 text-slate-400 hover:text-white">
              Lab Results
            </button>
            <button className="py-2 px-1 text-slate-400 hover:text-white">
              Documents
            </button>
            <button className="py-2 px-1 text-slate-400 hover:text-white">
              AI Assessments
            </button>
          </nav>
        </div>

        {/* Records Content */}
        <div className="space-y-6">
          {/* Recent Prescription */}
          <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
            <div className="flex items-start justify-between">
              <div className="flex">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Seasonal Allergy Medication
                  </h3>
                  <p className="text-slate-400 mb-2">
                    Prescribed by Dr. Mohamed Ali
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-slate-500">
                    <span>Jan 15, 2024</span>
                    <span>•</span>
                    <span>30-day supply</span>
                    <span>•</span>
                    <span className="text-green-400">Active</span>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </button>
                <button className="flex items-center px-3 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm">
                  <Share className="h-4 w-4 mr-1" />
                  Share
                </button>
              </div>
            </div>
          </div>

          {/* Lab Results */}
          <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
            <div className="flex items-start justify-between">
              <div className="flex">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mr-4">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Blood Work Panel
                  </h3>
                  <p className="text-slate-400 mb-2">
                    Requested by Dr. Aisha Ibrahim
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-slate-500">
                    <span>Jan 10, 2024</span>
                    <span>•</span>
                    <span>Complete Blood Count</span>
                    <span>•</span>
                    <span className="text-yellow-400">Results Pending</span>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button className="flex items-center px-3 py-2 bg-slate-700 text-slate-400 rounded-lg cursor-not-allowed text-sm">
                  <Clock className="h-4 w-4 mr-1" />
                  Pending
                </button>
              </div>
            </div>
          </div>

          {/* AI Assessment */}
          <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
            <div className="flex items-start justify-between">
              <div className="flex">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                  <MessageCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    AI Symptom Assessment
                  </h3>
                  <p className="text-slate-400 mb-2">
                    Cold & flu symptoms screening
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-slate-500">
                    <span>Jan 8, 2024</span>
                    <span>•</span>
                    <span>Low risk assessment</span>
                    <span>•</span>
                    <span className="text-green-400">Completed</span>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                  <FileText className="h-4 w-4 mr-1" />
                  View Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Profile Page Component (unchanged from your original)
  const ProfilePage = () => (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-white mb-8">
          Profile & Settings
        </h1>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Profile Info */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
              <h2 className="text-xl font-semibold text-white mb-6">
                Personal Information
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    defaultValue={mockUser.name}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    defaultValue={mockUser.email}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    defaultValue={mockUser.phone}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                  />
                </div>
              </div>

              <button className="mt-6 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
                Save Changes
              </button>
            </div>

            {/* Emergency Contact */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
              <h2 className="text-xl font-semibold text-white mb-6">
                Emergency Contact
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter emergency contact name"
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Relationship
                  </label>
                  <select className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-400">
                    <option>Select relationship</option>
                    <option>Spouse</option>
                    <option>Parent</option>
                    <option>Sibling</option>
                    <option>Child</option>
                    <option>Friend</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="Enter phone number"
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400"
                  />
                </div>
              </div>

              <button className="mt-6 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
                Save Emergency Contact
              </button>
            </div>
          </div>

          {/* Settings Sidebar */}
          <div className="space-y-6">
            {/* Language Settings */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">
                Language
              </h3>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="language"
                    value="en"
                    checked={language === "en"}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="mr-3"
                  />
                  <span className="text-slate-300">English</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="language"
                    value="dv"
                    checked={language === "dv"}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="mr-3"
                  />
                  <span className="text-slate-300">Dhivehi</span>
                </label>
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">
                Notifications
              </h3>
              <div className="space-y-4">
                <label className="flex items-center justify-between">
                  <span className="text-slate-300">Appointment reminders</span>
                  <input type="checkbox" defaultChecked className="rounded" />
                </label>
                <label className="flex items-center justify-between">
                  <span className="text-slate-300">Prescription alerts</span>
                  <input type="checkbox" defaultChecked className="rounded" />
                </label>
                <label className="flex items-center justify-between">
                  <span className="text-slate-300">Health tips</span>
                  <input type="checkbox" className="rounded" />
                </label>
              </div>
            </div>

            {/* Privacy */}
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">
                Privacy & Security
              </h3>
              <div className="space-y-3">
                <button className="w-full text-left p-3 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
                  Privacy Policy
                </button>
                <button className="w-full text-left p-3 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
                  Data & Consent
                </button>
                <button className="w-full text-left p-3 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
                  Two-Factor Auth
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render current page
  const renderCurrentPage = () => {
    switch (currentPage) {
      case "home":
        return <HomePage />;
      case "chat":
        return <ChatPage />;
      case "appointments":
        return <AppointmentsPage />;
      case "records":
        return <RecordsPage />;
      case "profile":
        return <ProfilePage />;
      case "settings":
        return <ProfilePage />;
      case "bookappointment":
        return <BookAppointmentPage />;
      default:
        return <HomePage />;
    }
  };

  return <div className="min-h-screen bg-slate-950">{renderCurrentPage()}</div>;
}

export default Dashboard;
